import fiftyFiftyImage1 from '@public/images/shipsticks/fifty-fifty-1.png'
import fiftyFiftyImage3 from '@public/images/shipsticks/fifty-fifty-3.jpg'
import fiftyFiftyImage4 from '@public/images/shipsticks/fifty-fifty-4.jpg'
import fiftyFiftyImage5 from '@public/images/shipsticks/fifty-fifty-5.jpg'

import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { TestimonialsProps } from 'lib/components/Testimonials/Testimonials'

import { StructuredData } from '@/utils/brand'

const structuredData: StructuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://www.shipsticks.com/#organization',
      name: 'ShipSticks',
      url: 'https://www.shipsticks.com/',
      sameAs: [
        'https://www.facebook.com/shipsticks',
        'https://www.instagram.com/shipsticks/',
        'https://www.linkedin.com/company/ship-sticks-llc',
        'https://www.youtube.com/@shipstickscom',
        'https://x.com/shipsticks',
        'https://www.tiktok.com/@shipsticks',
        'https://en.wikipedia.org/wiki/Ship_Sticks',
      ],
      logo: {
        '@type': 'ImageObject',
        '@id': 'https://www.shipsticks.com/#logo',
        url: 'https://media.shipsticks.com/7991/logo/full-logo_5fd063_231f20%402x.png',
        caption: 'ShipSticks',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '11400 N. Jog Road, Suite 300',
        addressLocality: 'Palm Beach Gardens',
        addressRegion: 'Florida',
        postalCode: '33418',
        addressCountry: 'US',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-561-429-3900',
        email: 'customersupport@shipsticks.com',
        contactType: 'customer service',
        areaServed: 'US',
        availableLanguage: 'English',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://www.shipsticks.com/#website',
      url: 'https://www.shipsticks.com/',
      name: 'ShipSticks',
      description: '#1 Golf Club Shipping Service',
      publisher: {
        '@id': 'https://www.shipsticks.com/#organization',
      },
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.shipsticks.com/blog/?s={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'ImageObject',
      '@id': 'https://www.shipsticks.com/#primaryimage',
      url: 'https://media.shipsticks.com/ship-sticks-og-image.png',
    },
    {
      '@type': 'WebPage',
      '@id': 'https://www.shipsticks.com/#webpage',
      url: 'https://www.shipsticks.com/',
      name: 'ShipSticks: Ship Golf Clubs Door-to-Door',
      inLanguage: 'en-US',
      isPartOf: {
        '@id': 'https://www.shipsticks.com/#webpage',
      },
      about: {
        '@id': 'https://www.shipsticks.com/#organization',
      },
      primaryImageOfPage: {
        '@id': 'https://www.shipsticks.com/#primaryimage',
      },
      description:
        'Ship your golf clubs ahead with ShipSticks and skip the airport stress. ' +
        'Enjoy savings up to 60% lower than regular carrier costs.',
    },
    {
      '@type': 'Product',
      '@id': 'https://www.shipsticks.com/#product',
      url: 'https://www.shipsticks.com/',
      name: 'Golf Club Shipping by ShipSticks',
      description:
        'Ship your golf clubs ahead with ShipSticks and skip the airport stress. ' +
        'Enjoy savings up to 60% lower than regular carrier costs.',
      image: 'https://media.shipsticks.com/7991/logo/full-logo_5fd063_231f20%402x.png',
      brand: {
        '@type': 'Organization',
        '@id': 'https://www.shipsticks.com/#organization',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: 4.7,
        reviewCount: 42390,
        bestRating: 5,
      },
    },
  ],
} as const

export interface VideoMeta {
  header?: string | React.ReactNode
  videoUrl: string
  caption?: string
  body?: string
  button?: {
    text: string
    link: {
      href: string
    }
  }
}

export interface HomePageContent {
  blogMeta: {
    buttonText: string
    subheader: string
  }
  heroMeta: {
    header: React.ReactNode
    subheader: string
    image: string
    stepsHeader: React.ReactNode
    steps: {
      header: string
      subheader: string
    }[]
  }
  fiftyFiftiesMeta: FiftyFiftyProps[]
  videoMeta?: VideoMeta
  testimonialsMeta: TestimonialsProps
  structuredData: typeof structuredData
}

const blogMeta = {
  buttonText: 'Continue reading',
  subheader:
    'Explore trending golf destinations, get insider travel tips, ' +
    "and see what's new in the world of golf.",
}

const heroMeta = {
  header: <>Skip Baggage Claim.</>,
  subheader: 'Ship your golf clubs instead.',
  image: '/images/shipsticks/hero-background.jpg',
  stepsHeader: (
    <>
      Ship your items in just three <b>easy steps.</b>
    </>
  ),
  steps: [
    {
      header: 'Book',
      subheader:
        'Easily book online, through the app, or over the phone - whatever works best for you.',
    },
    {
      header: 'Pack',
      subheader: `
          Pack your bags as you would for airline travel and attach your shipping label.
          We'll take it from there.
        `,
    },
    {
      header: 'Play',
      subheader: `
          Hop off the plane, slip on your spikes, and head to the course. Your gear will be waiting.
        `,
    },
  ],
}

const fiftyFiftiesMeta: FiftyFiftyProps[] = [
  {
    header: (
      <>
        Great golf trips <strong>start here.</strong>
      </>
    ),
    body: `
      Imagine a golf trip where getting there and back is actually the easy part - no lugging,
      no lines, and no "your bag didn't make it on the plane". Just stress-free travel designed
      for golfers like you.
    `,
    image: {
      src: fiftyFiftyImage1,
    },
    button: {
      text: 'Get started',
      action: 'booking-widget-redirect',
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        See <strong>how simple </strong>it is to ship.
      </>
    ),
    body: `
      Just pack up your clubs as you would for a flight, and we’ll pick them up from your home,
      office - even the golf course. Or, you can drop them off before you head out.
    `,
    videoUrl: 'https://www.youtube.com/watch?v=fuvtY9vlfGs',
    layout: 'image-left',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        Golf travel, <strong>transformed.</strong>
      </>
    ),
    body: `
      Avoid time wasted at the airport, excessive baggage fees, the costly SUV to fit your gear,
      or resorting to rentals. Arrive at the course relaxed and ready to play
      - to the envy of your foursome.
    `,
    image: {
      src: fiftyFiftyImage3,
    },
    button: {
      text: 'Try it out',
      action: 'booking-widget-redirect',
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        We treat your clubs <strong>like our own.</strong>
      </>
    ),
    body: `
      With real-time tracking, complimentary insurance, and dedicated customer service,
       your clubs get the royal treatment - something you don’t see on the tarmac.
    `,
    image: {
      src: fiftyFiftyImage4,
    },
    button: {
      text: 'What we offer',
      link: {
        href: '/guarantee',
      },
    },
    layout: 'image-left',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        Your golf trip, <strong>your way.</strong>
      </>
    ),
    body: `
      We ship worldwide and operate on your schedule. Whether you want your clubs waiting at
      the first tee, sent home after the last putt drops, or delivered to multiple
      stops, we’ve got you covered.
    `,
    image: {
      src: fiftyFiftyImage5,
    },
    button: {
      text: 'Discover more',
      link: {
        href: 'https://www.shipsticks.com/blog/travel/',
      },
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
]

const videoMeta: VideoMeta = {
  header: 'Shipping Your Gear with Ship Sticks',
  videoUrl: 'https://www.youtube.com/watch?v=puTgjmds_Gs',
  caption: 'How ShipSticks Works',
  body: `
    Ship your golf clubs, skis, or luggage ahead of time and skip
    the hassle of traveling with them. We'll pick up your gear or
    guide you to a drop-off option that fits your schedule. It
    arrives safely at your destination so you can travel hands-free.
  `,
  button: {
    text: 'Watch now',
    link: {
      href: 'https://www.youtube.com/watch?v=puTgjmds_Gs',
    },
  },
}

const testimonialsMeta: TestimonialsProps = {
  title: (
    <>
      What our customers <strong>have&nbsp;to&nbsp;say</strong>
    </>
  ),
  testimonials: [
    {
      stars: 5,
      quote:
        'It was GREAT to not worry about dragging our golf clubs through airports, ' +
        'rental cars and hotels.',
      attribution: {
        icon: 'golf',
        name: 'Bob',
      },
    },
    {
      stars: 5,
      quote: "Couldn't have been easier, the clubs got there and back on time, no damage, perfect.",
      attribution: {
        icon: 'golf',
        name: 'Tom',
      },
    },
    {
      stars: 5,
      quote: 'This service was great! Very easy, timely and tracked every step of the way',
      attribution: {
        icon: 'golf',
        name: 'Sean',
      },
    },
    {
      stars: 4,
      quote: 'Great customer service…they responded to my questions quickly, even over the weekend',
      attribution: {
        icon: 'golf',
        name: 'Vincent',
      },
    },
    {
      stars: 5,
      quote:
        'I have used ShipSticks at least a half dozen times both in the US and Scotland, ' +
        'each time they have exceeded my expectations',
      attribution: {
        icon: 'golf',
        name: 'Greg',
      },
    },
    {
      stars: 4,
      quote:
        'Convenient and easy. Worked out great to ship my clubs home after my trip to ' +
        'Bandon Dunes.',
      attribution: {
        icon: 'golf',
        name: 'Ken',
      },
    },
  ],
}

const content: HomePageContent = {
  blogMeta,
  heroMeta,
  fiftyFiftiesMeta,
  videoMeta,
  testimonialsMeta,
  structuredData,
}

export default content
